import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime, formatCurrency, laneLabel, prettify } from "@/lib/format";
import {
  CASE_STATUSES, SERVICE_LANES, URGENCY_LEVELS, REVENUE_TYPES, REVENUE_STATUSES,
  COMMUNICATION_TYPES, DIRECTIONS, RELATIONSHIPS, HOW_FOUND_OPTIONS, TONE, optOf, labelOf,
} from "@/config/caseOptions";
import { CaseStepBadge } from "@/pages/Cases";
import NotesPanel from "@/components/NotesPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import ServiceLaneWorkflows from "@/components/ServiceLaneWorkflows";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FolderHeart, ChevronLeft, MoonStar, Save, Phone, Mail, User, Users, HeartHandshake,
  Route as RouteIcon, ListChecks, StickyNote, FileText, Handshake, DollarSign,
  MessageSquare, History, Plus, AlertTriangle, CheckCircle2, Star, Trash2, Pencil,
  CalendarClock, Building2, Crown, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const TONE_DOT = { case_created: "#1C4358", task: "#9A6516", consultation: "#1C4358", communication: "#256B4B", referral: "#1C4358", revenue: "#256B4B", document: "#4B5563", note: "#9C1E22" };
const TIMELINE_ICON = { case_created: FolderHeart, task: ListChecks, consultation: CalendarClock, communication: MessageSquare, referral: Handshake, revenue: DollarSign, document: FileText, note: StickyNote };

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-0.5">{label}</div>
    <div className="text-sm text-[#252221]">{children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

const SectionCard = ({ title, icon: Icon, action, children, testid }) => (
  <Card className="p-4 sm:p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="font-display font-semibold text-[#252221] flex items-center gap-2">
        {Icon && <Icon size={17} className="text-[#1C4358]" />} {title}
      </h3>
      {action}
    </div>
    {children}
  </Card>
);

const TABS = [
  { value: "overview", label: "Overview", icon: FolderHeart },
  { value: "contacts", label: "Contacts", icon: Users },
  { value: "loved_one", label: "Loved One", icon: HeartHandshake },
  { value: "service_lanes", label: "Service Lanes", icon: RouteIcon },
  { value: "tasks", label: "Tasks", icon: ListChecks },
  { value: "notes", label: "Notes", icon: StickyNote },
  { value: "documents", label: "Documents", icon: FileText },
  { value: "partners", label: "Partners", icon: Handshake },
  { value: "revenue", label: "Revenue", icon: DollarSign },
  { value: "communications", label: "Communications", icon: MessageSquare },
  { value: "timeline", label: "Timeline", icon: History },
];

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("cases");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [partnersAll, setPartnersAll] = useState([]);
  const [savingStep, setSavingStep] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/cases/${id}/full`);
      setData(d);
    } catch (e) {
      toast.error("Could not load this Family Case");
      navigate("/cases");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data.items || [])).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
    api.get("/partners").then((r) => setPartnersAll(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const c = data?.case;
  const staffName = (uid) => staff.find((s) => s.id === uid)?.full_name || data?.case?.assigned_user_name || "Unassigned";
  const partnerName = (p) => p.company_name || p.name || p.contact_name || "Partner";

  const saveCase = async (partial, msg = "Family Case updated") => {
    try {
      const { data: updated } = await api.put(`/cases/${id}`, partial);
      setData((prev) => ({ ...prev, case: { ...prev.case, ...updated } }));
      if (msg) toast.success(msg);
      return updated;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      throw err;
    }
  };

  const changeStep = async (v) => {
    setSavingStep(true);
    try { await saveCase({ case_status: v }, "Current Step updated"); await load(); } finally { setSavingStep(false); }
  };

  const moveToFollowup = async () => {
    try {
      await api.post(`/cases/${id}/move-to-followup`, {});
      toast.success("Moved to follow-up");
      await load();
    } catch (err) { toast.error("Could not move to follow-up"); }
  };

  const toggleChecklist = async (itemId) => {
    const list = (c.completion_checklist || []).map((it) =>
      it.id === itemId ? { ...it, done: !it.done, done_at: !it.done ? new Date().toISOString() : null } : it);
    setData((prev) => ({ ...prev, case: { ...prev.case, completion_checklist: list } }));
    try { await api.put(`/cases/${id}`, { completion_checklist: list }); } catch { toast.error("Could not update checklist"); load(); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (!c) return null;

  const checklist = c.completion_checklist || [];
  const checklistDone = checklist.filter((i) => i.done).length;

  return (
    <div data-testid="case-detail-page">
      {/* Breadcrumb + back */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
        <Link to="/cases" className="inline-flex items-center gap-1 hover:text-[#1C4358]" data-testid="back-to-cases">
          <ChevronLeft size={16} /> Family Cases
        </Link>
        <span>/</span>
        <span className="text-[#252221] font-medium truncate">{c.case_name || c.family_name}</span>
      </div>

      {/* Header */}
      <Card className="p-4 sm:p-5 border-[#E6EDF2] mb-4" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-bold text-[#252221]" data-testid="case-title">{c.case_name || c.family_name}</h2>
              <CaseStepBadge value={c.case_status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
              <span className="font-mono text-xs">{c.case_number}</span>
              {c.loved_one_name && <span className="inline-flex items-center gap-1"><HeartHandshake size={13} /> {c.loved_one_name}</span>}
              {c.service_lane && <span className="inline-flex items-center gap-1"><RouteIcon size={13} /> {laneLabel(c.service_lane)}</span>}
              <span className="inline-flex items-center gap-1"><User size={13} /> {c.assigned_user_name || staffName(c.assigned_user_id)}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-[12px] text-muted-foreground hidden sm:block">Current Step</Label>
              <Select value={c.case_status} onValueChange={changeStep} disabled={!canWrite || savingStep}>
                <SelectTrigger className="h-9 w-[210px]" data-testid="case-step-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[320px]">{CASE_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {canWrite && (
              <Button variant="outline" onClick={moveToFollowup} data-testid="move-to-followup-button"
                className="border-[#E6EDF2] text-[#9A6516]">
                <MoonStar size={15} className="mr-1.5" /> Move to follow-up
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto mmm-scroll -mx-1 px-1">
          <TabsList className="bg-[#F7FAFC] border border-[#E6EDF2] h-auto flex-nowrap w-max">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}
                className="data-[state=active]:bg-white data-[state=active]:text-[#1C4358] data-[state=active]:shadow-sm whitespace-nowrap">
                <t.icon size={14} className="mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-4" data-testid="overview-tab">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <SectionCard title="Family Case overview" icon={FolderHeart} testid="overview-card">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <Field label="Loved one">{data.loved_one?.name || c.loved_one_name}</Field>
                  <Field label="Primary contact">
                    {data.primary_contact ? [data.primary_contact.first_name, data.primary_contact.last_name].filter(Boolean).join(" ") || c.family_name : c.family_name}
                  </Field>
                  <Field label="How they found us">{c.how_found ? labelOf(HOW_FOUND_OPTIONS, c.how_found) : prettify(c.how_found)}</Field>
                  <Field label="Current Step"><CaseStepBadge value={c.case_status} /></Field>
                  <Field label="Urgency">{c.urgency_level ? labelOf(URGENCY_LEVELS, c.urgency_level) : null}</Field>
                  <Field label="Assigned team member">{c.assigned_user_name || staffName(c.assigned_user_id)}</Field>
                  <Field label="Service lane">{c.service_lane ? laneLabel(c.service_lane) : null}</Field>
                  <Field label="Branch">{branches.find((b) => b.id === c.branch_id)?.name}</Field>
                  <Field label="Opened">{formatDate(c.created_at)}</Field>
                </div>
                <div className="mt-4">
                  <Field label="Main issue">{c.main_issue || c.summary}</Field>
                </div>
              </SectionCard>

              <SectionCard title={`Completion checklist (${checklistDone}/${checklist.length})`} icon={ClipboardList} testid="checklist-card">
                {checklist.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No checklist items.</p>
                ) : (
                  <div className="space-y-2">
                    {checklist.map((it) => (
                      <label key={it.id} className="flex items-center gap-2.5 cursor-pointer group" data-testid="checklist-item">
                        <Checkbox checked={!!it.done} onCheckedChange={() => canWrite && toggleChecklist(it.id)} disabled={!canWrite} data-testid="checklist-checkbox" />
                        <span className={`text-sm ${it.done ? "line-through text-muted-foreground" : "text-[#252221]"}`}>{it.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            <div className="space-y-4">
              <SectionCard title="Next task" icon={ListChecks} testid="next-task-card">
                {data.next_task ? (
                  <div className="rounded-lg border border-[#E6EDF2] p-3 bg-[#F7FAFC]">
                    <div className="font-medium text-sm text-[#252221]">{data.next_task.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      {data.next_task.due_date && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {formatDate(data.next_task.due_date)}</span>}
                      {data.next_task.priority && <span>{prettify(data.next_task.priority)}</span>}
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground">No open tasks. <button className="text-[#1C4358] hover:underline" onClick={() => setTab("tasks")}>Add a task</button>.</p>}
                <div className="text-xs text-muted-foreground mt-2">{data.open_tasks_count || 0} open task{(data.open_tasks_count || 0) === 1 ? "" : "s"}</div>
              </SectionCard>

              <SectionCard title="Documents needed" icon={FileText} testid="docs-needed-card">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">On file</span>
                  <span className="font-semibold text-[#252221]">{data.documents_count || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-muted-foreground">Open requests</span>
                  <span className="font-semibold text-[#9A6516]">{(data.open_document_requests || []).length}</span>
                </div>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setTab("documents")} data-testid="goto-documents">Open documents</Button>
              </SectionCard>

              <SectionCard title="Revenue opportunities" icon={DollarSign} testid="revenue-summary-card">
                <div className="text-2xl font-semibold tabular-nums text-[#252221]">{formatCurrency(data.revenue_total || 0)}</div>
                <div className="text-xs text-muted-foreground">{(data.revenue || []).length} opportunit{(data.revenue || []).length === 1 ? "y" : "ies"} · {formatCurrency(data.revenue_won || 0)} won</div>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setTab("revenue")} data-testid="goto-revenue">Open revenue</Button>
              </SectionCard>

              <SectionCard title="Recent timeline" icon={History} testid="overview-timeline-card">
                <TimelineFeed events={(data.timeline || []).slice(0, 6)} compact />
                {(data.timeline || []).length > 6 && (
                  <button className="text-xs text-[#1C4358] hover:underline mt-2" onClick={() => setTab("timeline")}>View full timeline</button>
                )}
              </SectionCard>
            </div>
          </div>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-4" data-testid="contacts-tab">
          <ContactsTab caseId={id} contacts={data.contacts} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* LOVED ONE */}
        <TabsContent value="loved_one" className="mt-4" data-testid="loved_one-tab">
          <LovedOneTab caseId={id} lovedOne={data.loved_one} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* SERVICE LANES */}
        <TabsContent value="service_lanes" className="mt-4 space-y-4" data-testid="service_lanes-tab">
          <ServiceLaneWorkflows caseId={id} staff={staff} workflows={data.service_lane_workflows} canWrite={canWrite} onChange={load} />
          <ServiceLanesTab c={c} canWrite={canWrite} onSave={saveCase} />
        </TabsContent>

        {/* TASKS */}
        <TabsContent value="tasks" className="mt-4" data-testid="tasks-tab">
          <TasksTab caseId={id} branchId={c.branch_id} tasks={data.tasks} staff={staff} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-4" data-testid="notes-tab">
          <NotesPanel relatedType="cases" relatedId={id} branchId={c.branch_id} />
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-4" data-testid="documents-tab">
          <DocumentsPanel relatedType="cases" relatedId={id} branchId={c.branch_id} />
        </TabsContent>

        {/* PARTNERS */}
        <TabsContent value="partners" className="mt-4" data-testid="partners-tab">
          <PartnersTab caseId={id} partners={data.partners} partnersAll={partnersAll} defaultLane={c.service_lane} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* REVENUE */}
        <TabsContent value="revenue" className="mt-4" data-testid="revenue-tab">
          <RevenueTab caseId={id} branchId={c.branch_id} familyName={c.family_name} defaultLane={c.service_lane} revenue={data.revenue} total={data.revenue_total} won={data.revenue_won} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* COMMUNICATIONS */}
        <TabsContent value="communications" className="mt-4" data-testid="communications-tab">
          <CommunicationsTab caseId={id} communications={data.communications} appointments={data.appointments} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-4" data-testid="timeline-tab">
          <SectionCard title="Case timeline" icon={History}>
            <TimelineFeed events={data.timeline || []} />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Timeline ---------------- */
const TimelineFeed = ({ events, compact }) => {
  if (!events || events.length === 0) return <p className="text-sm text-muted-foreground py-2">No activity yet.</p>;
  return (
    <div className="relative pl-4">
      <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E6EDF2]" />
      <div className="space-y-3">
        {events.map((e, i) => {
          const Icon = TIMELINE_ICON[e.type] || History;
          return (
            <div key={i} className="relative flex items-start gap-3" data-testid="timeline-event">
              <span className="relative z-10 h-3.5 w-3.5 rounded-full border-2 border-white mt-0.5 shrink-0" style={{ backgroundColor: TONE_DOT[e.type] || "#9BB9CE" }} />
              <div className="min-w-0 flex-1 -mt-0.5">
                <div className="flex items-center gap-1.5 text-sm text-[#252221]">
                  <Icon size={13} className="text-[#1C4358] shrink-0" />
                  <span className="truncate">{e.title}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{formatDateTime(e.at)}{e.detail ? ` · ${prettify(e.detail)}` : ""}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- Contacts ---------------- */
const emptyContact = { first_name: "", last_name: "", relationship: "", phone: "", email: "", is_primary_contact: false, is_decision_maker: false, notes: "" };
const ContactsTab = ({ caseId, contacts, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyContact);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const openNew = () => { setForm(emptyContact); setEditing(null); setOpen(true); };
  const openEdit = (ct) => { setForm({ ...emptyContact, ...ct }); setEditing(ct.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name && !form.last_name) { toast.error("Contact name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/cases/${caseId}/contacts/${editing}`, form);
      else await api.post(`/cases/${caseId}/contacts`, form);
      toast.success("Contact saved");
      setOpen(false); onChange();
    } catch (err) { toast.error("Could not save contact"); } finally { setBusy(false); }
  };
  const remove = async (cid) => { try { await api.delete(`/cases/${caseId}/contacts/${cid}`); toast.success("Contact removed"); onChange(); } catch { toast.error("Could not remove"); } };

  return (
    <SectionCard title="Family contacts" icon={Users} testid="contacts-card"
      action={canWrite && <Button size="sm" onClick={openNew} data-testid="add-contact-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> Add contact</Button>}>
      {(!contacts || contacts.length === 0) ? (
        <p className="text-sm text-muted-foreground py-2">No contacts yet. Add the primary family contact.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {contacts.map((ct) => (
            <div key={ct.id} className="rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="contact-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[#252221] flex items-center gap-1.5">
                    {[ct.first_name, ct.last_name].filter(Boolean).join(" ") || "Contact"}
                    {ct.is_primary_contact && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(28,67,88,0.10)", color: "#1C4358" }}><Star size={9} /> Primary</span>}
                    {ct.is_decision_maker && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(156,30,34,0.10)", color: "#9C1E22" }}><Crown size={9} /> Decision maker</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{ct.relationship ? labelOf(RELATIONSHIPS, ct.relationship) : ""}</div>
                  <div className="mt-1.5 space-y-0.5 text-sm">
                    {ct.phone && <a href={`tel:${ct.phone}`} className="flex items-center gap-1.5 text-[#1C4358] hover:underline"><Phone size={13} /> {ct.phone}</a>}
                    {ct.email && <a href={`mailto:${ct.email}`} className="flex items-center gap-1.5 text-[#1C4358] hover:underline"><Mail size={13} /> {ct.email}</a>}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(ct)} className="p-1.5 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]" data-testid="edit-contact-button"><Pencil size={14} /></button>
                    <button onClick={() => remove(ct.id)} className="p-1.5 rounded-lg hover:bg-[rgba(156,30,34,0.08)] text-[#9C1E22]" data-testid="delete-contact-button"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="contact-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">{editing ? "Edit contact" : "Add family contact"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">First name</Label><Input value={form.first_name} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} data-testid="contact-first-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Last name</Label><Input value={form.last_name} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} data-testid="contact-last-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Relationship</Label>
                <Select value={form.relationship || ""} onValueChange={(v) => setForm((p) => ({ ...p, relationship: v }))}>
                  <SelectTrigger data-testid="contact-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{RELATIONSHIPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} data-testid="contact-phone" /></div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} data-testid="contact-email" /></div>
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={!!form.is_primary_contact} onCheckedChange={(v) => setForm((p) => ({ ...p, is_primary_contact: !!v }))} data-testid="contact-is-primary" /> Primary contact</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={!!form.is_decision_maker} onCheckedChange={(v) => setForm((p) => ({ ...p, is_decision_maker: !!v }))} data-testid="contact-is-decision-maker" /> Decision maker</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-contact-button">{busy ? "Saving..." : "Save contact"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

/* ---------------- Loved One ---------------- */
const LovedOneTab = ({ caseId, lovedOne, canWrite, onChange }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(lovedOne || {}); }, [lovedOne]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/cases/${caseId}/loved-one`, {
        first_name: form.first_name, last_name: form.last_name, preferred_name: form.preferred_name,
        age: form.age ? Number(form.age) : undefined, current_living_situation: form.current_living_situation,
        mobility_concerns: form.mobility_concerns, safety_concerns: form.safety_concerns,
        memory_concerns: form.memory_concerns, financial_concerns: form.financial_concerns,
        current_support_system: form.current_support_system, poa_name: form.poa_name,
        poa_exists: form.poa_exists, decision_maker: form.decision_maker, notes: form.notes,
      });
      toast.success("Loved one profile saved");
      onChange();
    } catch { toast.error("Could not save profile"); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Loved one profile" icon={HeartHandshake} testid="loved-one-card"
      action={canWrite && <Button size="sm" onClick={save} disabled={busy} data-testid="save-loved-one-button" className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }}><Save size={15} className="mr-1" /> {busy ? "Saving..." : "Save"}</Button>}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label className="mb-1.5 block text-[13px]">First name</Label><Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} disabled={!canWrite} data-testid="lo-first-name" /></div>
        <div><Label className="mb-1.5 block text-[13px]">Last name</Label><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Preferred name</Label><Input value={form.preferred_name || ""} onChange={(e) => set("preferred_name", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Age</Label><Input type="number" value={form.age || ""} onChange={(e) => set("age", e.target.value)} disabled={!canWrite} /></div>
        <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Current living situation</Label><Input value={form.current_living_situation || ""} onChange={(e) => set("current_living_situation", e.target.value)} disabled={!canWrite} data-testid="lo-living-situation" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div><Label className="mb-1.5 block text-[13px]">Mobility concerns</Label><Textarea rows={2} value={form.mobility_concerns || ""} onChange={(e) => set("mobility_concerns", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Safety concerns</Label><Textarea rows={2} value={form.safety_concerns || ""} onChange={(e) => set("safety_concerns", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Memory concerns</Label><Textarea rows={2} value={form.memory_concerns || ""} onChange={(e) => set("memory_concerns", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Financial concerns</Label><Textarea rows={2} value={form.financial_concerns || ""} onChange={(e) => set("financial_concerns", e.target.value)} disabled={!canWrite} /></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
        <div><Label className="mb-1.5 block text-[13px]">Decision maker</Label><Input value={form.decision_maker || ""} onChange={(e) => set("decision_maker", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Power of attorney</Label><Input value={form.poa_name || ""} onChange={(e) => set("poa_name", e.target.value)} disabled={!canWrite} /></div>
        <div><Label className="mb-1.5 block text-[13px]">Support system</Label><Input value={form.current_support_system || ""} onChange={(e) => set("current_support_system", e.target.value)} disabled={!canWrite} /></div>
      </div>
      <div className="mt-3"><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} disabled={!canWrite} data-testid="lo-notes" /></div>
    </SectionCard>
  );
};

/* ---------------- Service Lanes ---------------- */
const INVOLVEMENT = [
  { key: "property_involved", label: "Property / home sale involved" },
  { key: "placement_involved", label: "Senior living placement involved" },
  { key: "home_repair_involved", label: "Home repairs / safety involved" },
  { key: "downsizing_involved", label: "Downsizing / estate involved" },
];
const ServiceLanesTab = ({ c, canWrite, onSave }) => {
  const [primary, setPrimary] = useState(c.service_lane || "");
  const [secondary, setSecondary] = useState(c.secondary_service_lanes || []);
  const [flags, setFlags] = useState({
    property_involved: !!c.property_involved, placement_involved: !!c.placement_involved,
    home_repair_involved: !!c.home_repair_involved, downsizing_involved: !!c.downsizing_involved,
  });
  const [busy, setBusy] = useState(false);
  const toggleSecondary = (v) => setSecondary((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  const save = async () => {
    setBusy(true);
    try { await onSave({ service_lane: primary || null, secondary_service_lanes: secondary, ...flags }, "Service lanes saved"); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Lane setup & involvement" icon={RouteIcon} testid="service-lanes-card"
      action={canWrite && <Button size="sm" onClick={save} disabled={busy} data-testid="save-lanes-button" className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }}><Save size={15} className="mr-1" /> {busy ? "Saving..." : "Save"}</Button>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label className="mb-1.5 block text-[13px]">Primary service lane</Label>
          <Select value={primary || ""} onValueChange={setPrimary} disabled={!canWrite}>
            <SelectTrigger data-testid="primary-lane-select"><SelectValue placeholder="Select primary lane" /></SelectTrigger>
            <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>

          <Label className="mb-1.5 mt-4 block text-[13px]">Secondary service lanes</Label>
          <div className="space-y-2">
            {SERVICE_LANES.filter((l) => l.value !== primary).map((l) => (
              <label key={l.value} className="flex items-center gap-2 text-sm cursor-pointer" data-testid="secondary-lane-option">
                <Checkbox checked={secondary.includes(l.value)} onCheckedChange={() => canWrite && toggleSecondary(l.value)} disabled={!canWrite} />
                {l.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block text-[13px]">What this case involves</Label>
          <div className="space-y-3 mt-1">
            {INVOLVEMENT.map((f) => (
              <div key={f.key} className="flex items-center justify-between rounded-lg border border-[#E6EDF2] px-3 py-2.5" data-testid={`involve-${f.key}`}>
                <span className="text-sm text-[#252221]">{f.label}</span>
                <Switch checked={!!flags[f.key]} onCheckedChange={(v) => canWrite && setFlags((p) => ({ ...p, [f.key]: v }))} disabled={!canWrite} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

/* ---------------- Tasks ---------------- */
const TasksTab = ({ caseId, branchId, tasks, staff, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "", priority: "medium", assignee_id: "" });
  const [busy, setBusy] = useState(false);
  const staffName = (id) => staff.find((s) => s.id === id)?.full_name || "Unassigned";

  const add = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Task title is required"); return; }
    setBusy(true);
    try {
      await api.post("/tasks", {
        title: form.title, due_date: form.due_date || undefined, priority: form.priority, status: "todo",
        assignee_id: form.assignee_id || undefined, assigned_to_user_id: form.assignee_id || undefined,
        case_id: caseId, related_type: "case", related_id: caseId, branch_id: branchId,
      });
      toast.success("Task added"); setOpen(false); setForm({ title: "", due_date: "", priority: "medium", assignee_id: "" }); onChange();
    } catch { toast.error("Could not add task"); } finally { setBusy(false); }
  };
  const toggle = async (t) => {
    try { await api.put(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" }); onChange(); } catch { toast.error("Could not update task"); }
  };
  const sorted = [...(tasks || [])].sort((a, b) => (a.status === "done") - (b.status === "done") || ((a.due_date || "") > (b.due_date || "") ? 1 : -1));

  return (
    <SectionCard title="Tasks" icon={ListChecks} testid="tasks-card"
      action={canWrite && <Button size="sm" onClick={() => setOpen(true)} data-testid="add-task-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> Add task</Button>}>
      {(!sorted || sorted.length === 0) ? (
        <p className="text-sm text-muted-foreground py-2">No tasks yet. Add a follow-up to keep this case moving.</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border border-[#E6EDF2] p-3 bg-white" data-testid="task-row">
              <Checkbox checked={t.status === "done"} onCheckedChange={() => canWrite && toggle(t)} disabled={!canWrite} data-testid="task-toggle" />
              <div className="min-w-0 flex-1">
                <div className={`text-sm ${t.status === "done" ? "line-through text-muted-foreground" : "text-[#252221] font-medium"}`}>{t.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  {t.due_date && <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> {formatDate(t.due_date)}</span>}
                  <span>{staffName(t.assignee_id || t.assigned_to_user_id)}</span>
                </div>
              </div>
              {t.priority === "high" && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(156,30,34,0.10)", color: "#9C1E22" }}><AlertTriangle size={10} /> High</span>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="task-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">Add task</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <div><Label className="mb-1.5 block text-[13px]">Task title</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="task-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} data-testid="task-due-input" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Assign to</Label>
              <Select value={form.assignee_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, assignee_id: v }))}>
                <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Choose teammate" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-task-button">{busy ? "Adding..." : "Add task"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

/* ---------------- Partners ---------------- */
const PartnersTab = ({ caseId, partners, partnersAll, defaultLane, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ partner_id: "", referral_type: defaultLane || "", notes: "" });
  const [busy, setBusy] = useState(false);
  const pName = (p) => p.company_name || p.name || p.contact_name || "Partner";
  const submit = async (e) => {
    e.preventDefault();
    if (!form.partner_id) { toast.error("Choose a partner"); return; }
    setBusy(true);
    try { await api.post(`/cases/${caseId}/referrals`, form); toast.success("Partner assigned"); setOpen(false); setForm({ partner_id: "", referral_type: defaultLane || "", notes: "" }); onChange(); }
    catch { toast.error("Could not assign partner"); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Partners & vendors" icon={Handshake} testid="partners-card"
      action={canWrite && <Button size="sm" onClick={() => setOpen(true)} data-testid="assign-partner-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> Assign partner</Button>}>
      {(!partners || partners.length === 0) ? (
        <p className="text-sm text-muted-foreground py-2">No partners assigned yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {partners.map((p) => (
            <div key={p.id} className="rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="partner-card">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-[#252221]">{p.partner_name}</div>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }}>{prettify(p.status)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.partner_category ? prettify(p.partner_category) : ""}{p.referral_type ? ` · ${laneLabel(p.referral_type)}` : ""}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-3 text-sm">
                {p.partner_phone && <a href={`tel:${p.partner_phone}`} className="flex items-center gap-1 text-[#1C4358] hover:underline"><Phone size={12} /> {p.partner_phone}</a>}
                {p.partner_email && <a href={`mailto:${p.partner_email}`} className="flex items-center gap-1 text-[#1C4358] hover:underline"><Mail size={12} /> Email</a>}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="partner-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">Assign partner / vendor</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="mb-1.5 block text-[13px]">Partner</Label>
              <Select value={form.partner_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, partner_id: v }))}>
                <SelectTrigger data-testid="partner-select"><SelectValue placeholder="Choose partner" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">{partnersAll.map((p) => <SelectItem key={p.id} value={p.id}>{pName(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Referral type</Label>
              <Select value={form.referral_type || ""} onValueChange={(v) => setForm((p) => ({ ...p, referral_type: v }))}>
                <SelectTrigger data-testid="referral-type-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="referral-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-partner-button">{busy ? "Assigning..." : "Assign"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

/* ---------------- Revenue ---------------- */
const RevenueTab = ({ caseId, branchId, familyName, defaultLane, revenue, total, won, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", revenue_type: "service_fee", service_lane: defaultLane || "", status: "potential" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/revenue", {
        case_id: caseId, family_name: familyName, amount: Number(form.amount) || 0,
        revenue_type: form.revenue_type, service_lane: form.service_lane || undefined,
        status: form.status, branch_id: branchId,
      });
      toast.success("Revenue opportunity added"); setOpen(false);
      setForm({ amount: "", revenue_type: "service_fee", service_lane: defaultLane || "", status: "potential" }); onChange();
    } catch { toast.error("Could not add revenue"); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Revenue opportunities" icon={DollarSign} testid="revenue-card"
      action={canWrite && <Button size="sm" onClick={() => setOpen(true)} data-testid="add-revenue-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> Add opportunity</Button>}>
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="rounded-lg border border-[#E6EDF2] px-4 py-2 bg-[#F7FAFC]"><div className="text-xs text-muted-foreground">Total pipeline</div><div className="text-xl font-semibold tabular-nums text-[#252221]" data-testid="revenue-total">{formatCurrency(total || 0)}</div></div>
        <div className="rounded-lg border border-[#E6EDF2] px-4 py-2 bg-[#F7FAFC]"><div className="text-xs text-muted-foreground">Won</div><div className="text-xl font-semibold tabular-nums" style={{ color: "#256B4B" }}>{formatCurrency(won || 0)}</div></div>
      </div>
      {(!revenue || revenue.length === 0) ? (
        <p className="text-sm text-muted-foreground py-2">No revenue opportunities yet.</p>
      ) : (
        <div className="space-y-2">
          {revenue.map((r) => {
            const so = optOf(REVENUE_STATUSES, r.status); const t = TONE[so?.tone || "gray"];
            return (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E6EDF2] p-3 bg-white" data-testid="revenue-row">
                <div>
                  <div className="font-medium text-[#252221]">{labelOf(REVENUE_TYPES, r.revenue_type)}{r.service_lane ? ` · ${laneLabel(r.service_lane)}` : ""}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold tabular-nums text-[#252221]">{formatCurrency(r.amount || r.expected_amount || 0)}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full border" style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }}>{so?.label || prettify(r.status)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="revenue-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">Add revenue opportunity</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Expected amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} placeholder="0" data-testid="revenue-amount" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Type</Label>
                <Select value={form.revenue_type} onValueChange={(v) => setForm((p) => ({ ...p, revenue_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REVENUE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                <Select value={form.service_lane || ""} onValueChange={(v) => setForm((p) => ({ ...p, service_lane: v }))}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REVENUE_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-revenue-button">{busy ? "Adding..." : "Add"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

/* ---------------- Communications ---------------- */
const CommunicationsTab = ({ caseId, communications, appointments, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ communication_type: "call", direction: "outbound", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/cases/${caseId}/communications`, form); toast.success("Communication logged"); setOpen(false); setForm({ communication_type: "call", direction: "outbound", subject: "", body: "" }); onChange(); }
    catch { toast.error("Could not log communication"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <SectionCard title="Communications log" icon={MessageSquare} testid="communications-card"
        action={canWrite && <Button size="sm" onClick={() => setOpen(true)} data-testid="log-communication-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> Log communication</Button>}>
        {(!communications || communications.length === 0) ? (
          <p className="text-sm text-muted-foreground py-2">No communications logged yet.</p>
        ) : (
          <div className="space-y-2.5">
            {communications.map((m) => (
              <div key={m.id} className="rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="communication-row">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(28,67,88,0.08)", color: "#1C4358" }}>{labelOf(COMMUNICATION_TYPES, m.communication_type)}</span>
                  <span className="text-[11px] text-muted-foreground">{prettify(m.direction)}</span>
                  <span className="text-[11px] text-muted-foreground ml-auto">{formatDateTime(m.created_at)}</span>
                </div>
                {m.subject && <div className="text-sm font-medium text-[#252221]">{m.subject}</div>}
                {m.body && <p className="text-sm text-[#252221] whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                {m.author_name && <div className="text-[11px] text-muted-foreground mt-1">by {m.author_name}</div>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Appointments" icon={CalendarClock} testid="appointments-card">
        {(!appointments || appointments.length === 0) ? (
          <p className="text-sm text-muted-foreground py-2">No appointments linked to this case.</p>
        ) : (
          <div className="space-y-2">
            {appointments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#E6EDF2] p-3 bg-white" data-testid="appointment-row">
                <div>
                  <div className="text-sm font-medium text-[#252221]">{a.consultant_name ? `With ${a.consultant_name}` : "Consultation"}</div>
                  <div className="text-xs text-muted-foreground">{a.scheduled_at ? formatDateTime(a.scheduled_at) : "Not scheduled"}</div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }}>{prettify(a.status)}</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="communication-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">Log communication</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Type</Label>
                <Select value={form.communication_type} onValueChange={(v) => setForm((p) => ({ ...p, communication_type: v }))}>
                  <SelectTrigger data-testid="comm-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMUNICATION_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm((p) => ({ ...p, direction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DIRECTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Subject</Label><Input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} data-testid="comm-subject" /></div>
            <div><Label className="mb-1.5 block text-[13px]">Summary / notes</Label><Textarea rows={3} value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="comm-body" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-communication-button">{busy ? "Saving..." : "Log it"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
